import { useState, useCallback, useEffect, useRef } from 'react';

export type RadixSelectStatus = 'closed' | 'open' | 'closing';

export function useRadixSelectAnimation() {
  const [status, setStatus] = useState<RadixSelectStatus>('closed');
  const closingGuard = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finishClosing = useCallback(() => {
    if (!closingGuard.current) return;
    closingGuard.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStatus('closed');
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      closingGuard.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setStatus('open');
    } else {
      closingGuard.current = true;
      setStatus('closing');
      timeoutRef.current = setTimeout(finishClosing, 200);
    }
  }, [finishClosing]);

  const handleAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'exit' && e.currentTarget === e.target) {
      finishClosing();
    }
  }, [finishClosing]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  return { status, handleOpenChange, handleAnimationEnd };
}
